/**
 * RNR's Card, on the restyle's anatomy: a 10px sheet with one hairline, and — where it has a title
 * — a filled HEADER BAND as its first row.
 *
 * The band is the round's signature object and it is shared by both looks: Bouquet wears the
 * booth's red panel as a gradient, Tuxedo the same flat midnight under a 2px gold rule, and the
 * difference is entirely in the tokens `.fit-band` reads. Cards never nest inside cards; a card
 * that needs a card inside it is a list.
 */
import { Text, TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { useStore } from '@/store';
import * as React from 'react';
import { Platform, View } from 'react-native';

/**
 * The hairline a card carries, or the transparent one it keeps the width of.
 *
 * A tuxedo card at night is a flat sheet with no edge: the room is dark enough that the fill alone
 * separates it, and a hairline there reads as a box drawn round a surface. This used to be a CSS
 * rule (`html.dark[data-look='tuxedo'] .fit-card`), which meant native never got it. It is a
 * store read now, so both platforms do.
 *
 * `border-transparent` and not "no border": the header band's negative margins and its 9px radius
 * are both measured against a 1px edge, so taking the WIDTH away would shift the band a pixel.
 * Only the colour goes, which is exactly what the CSS rule did.
 */
export function useCardEdge(): string {
  const look = useStore((s) => s.look);
  const theme = useStore((s) => s.theme);
  return look === 'tuxedo' && theme === 'dark' ? 'border border-transparent' : 'border-border border';
}

/**
 * THE GARLAND's bulb geometry, per look — straight off `global.css`'s two garland rules.
 *
 * `period` is the CSS tile's own width and `offset`/`step` its first circle centre and spacing, so
 * the native dots land on the same pixels the web gradient does. `lit`/`core`/`unlit` are
 * diameters, read off the radii in those `radial-gradient()` stops. Bouquet alternates a lit bulb
 * with an unlit one at the half period; Tuxedo lights every bulb and spaces them wider — a
 * footlight rim, not a marquee.
 *
 * A WHOLE NUMBER OF PERIODS is what the strip is sized to, on both platforms: a bulb is a round
 * thing or it is a smear, and a repeating background cut wherever the card happened to end drew a
 * half one at each edge.
 */
const GARLAND = {
  bouquet: { period: 26, offset: 7, step: 13, alternate: true, lit: 6.4, core: 3.6, unlit: 4.4 },
  tuxedo: { period: 22, offset: 6, step: 22, alternate: false, lit: 4.6, core: 2.6, unlit: 4.6 },
} as const;

/** The 14px box both looks are drawn into, and the centre line the bulbs sit on. */
const GARLAND_H = 14;

function Card({
  className,
  moment,
  ...props
}: React.ComponentProps<typeof View> &
  React.RefAttributes<View> & {
    /**
     * A MOMENT: a card the round decorates with the garland — Bouquet's marquee bulbs, Tuxedo's
     * footlights — along the inside of its top edge.
     *
     * Exactly five cards in the app are one: the Home pairing card, the co-op card, a Sync HIT
     * verdict, the first revealed question, and the Score head-to-head. It is a claim that
     * something happened, so it means nothing if every card makes it.
     */
    moment?: boolean;
  }) {
  const edge = useCardEdge();
  return (
    <TextClassContext.Provider value="text-card-foreground">
      <View
        className={cn(
          'bg-card overflow-hidden rounded-lg',
          edge,
          /**
           * THE GARLAND IS DRAWN INSIDE THE CARD'S OWN BOX.
           *
           * It used to straddle the top edge from a wrapper — a sibling pulled up 7px — and the
           * half above the edge was clipped by whichever scroller the card was the first child
           * of, leaving a row of half bulbs on a seam with the end ones out past the corner
           * radius. A moment reserves the strip's full height as top padding instead and puts the
           * bulbs entirely within it, so the card's radius and its own `overflow-hidden` have
           * nothing left to cut. `CardHead`'s `-mt-4` still lands the band flush under the strip,
           * because it is measured against `CardContent`'s padding and not against the card's top.
           */
          moment && 'pt-[14px]',
          // Shadows are the one thing that does not survive the platform split intact.
          Platform.select({ web: 'shadow-sm shadow-black/5' }),
          className,
        )}
        {...props}>
        {/* Inset past the shoulder at both ends, so the first and last bulb are inside the curve. */}
        {moment ? <Garland className="absolute left-3 right-3 top-0 z-10" /> : null}
        {props.children}
      </View>
    </TextClassContext.Provider>
  );
}

/**
 * THE GARLAND on its own — Bouquet's marquee bulbs, Tuxedo's footlights — for the one moment the
 * board draws on the bare ground rather than on a card: the Sync HIT verdict, which both sheets
 * set as a plain block with the strip above it and no sheet behind it. `Card moment` uses this
 * same strip inside its own top edge, so there is exactly one 14px box either way.
 */
function Garland({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  const look = useStore((s) => s.look);
  const [width, setWidth] = React.useState(0);
  const g = GARLAND[look];

  /**
   * How many WHOLE tiles fit, measured rather than assumed.
   *
   * Both renderings are drawn at exactly that width and centred in the box, which is what ends the
   * row on a complete bulb at both ends: the gradient's last circle sits a few pixels inside its
   * own tile, so a strip that is a whole number of tiles wide can never cut one. The leftover —
   * under a tile, so at most 25px — is split evenly as air at the two ends.
   */
  const periods = width ? Math.max(1, Math.floor(width / g.period)) : 0;
  const bandWidth = periods * g.period;
  const count = periods * Math.round(g.period / g.step);

  /**
   * The web keeps the real thing and native gets dots.
   *
   * `.fit-garland` is a repeating radial gradient with a warm halo round every lit bulb, and the
   * halo is the object — it is what makes a bulb read as lit rather than as a dot. A View cannot
   * draw one, and this round adds no gradient dependency, so a phone gets flat circles on the same
   * period at the same diameters in the same `bulb-*` tokens. Honest rather than broken, and the
   * strip is the same 14px box either way so nothing in the layout knows which it got.
   */
  return (
    <View
      className={cn('h-[14px] items-center', className)}
      pointerEvents="none"
      onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
      {...props}>
      {!periods ? null : Platform.OS === 'web' ? (
        <View className="fit-garland h-[14px]" style={{ width: bandWidth }} />
      ) : (
        <View style={{ width: bandWidth, height: GARLAND_H }}>{bulbs(g, count)}</View>
      )}
    </View>
  );
}

/** The native rendering of one strip: `count` flat circles on the tile's own centres. */
function bulbs(g: (typeof GARLAND)[keyof typeof GARLAND], count: number) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => {
        const lit = !g.alternate || i % 2 === 0;
        const size = lit ? g.lit : g.unlit;
        const centre = g.offset + i * g.step;
        return (
          <View
            key={i}
            className={lit ? 'bg-bulb-on' : 'bg-bulb-off'}
            style={{
              position: 'absolute',
              left: centre - size / 2,
              top: GARLAND_H / 2 - size / 2,
              width: size,
              height: size,
              borderRadius: size / 2,
            }}>
            {lit ? (
              <View
                className="bg-bulb-core"
                style={{
                  position: 'absolute',
                  left: (size - g.core) / 2,
                  top: (size - g.core) / 2,
                  width: g.core,
                  height: g.core,
                  borderRadius: g.core / 2,
                }}
              />
            ) : null}
          </View>
        );
      })}
    </>
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  return <View className={cn('border-border flex-row flex-wrap items-center justify-between gap-3 border-b p-4', className)} {...props} />;
}

/**
 * THE HEADER BAND — a titled card's first row, bled out to the card's own edges.
 *
 * It MUST be the first child of `CardContent`, whose `p-4` the negative margins here are measured
 * against: the band is carried out to the padding box, which sits exactly inside the card's 1px
 * border, so its radius is the card's less that border — 9px against `rounded-lg`'s 10.
 *
 * Two shapes and no more. A string child is a title; `row` is the case where the board puts
 * something beside it — the mission's points chip, the head-to-head's two avatars, the question's
 * type badge and date. Either way every Text inside inherits band ink through the context, which
 * is what stops a caller having to remember `text-band-ink` on each of six nested pieces.
 *
 * Bouquet's band pads 12px and Tuxedo's 10px; both take 12 here. A two-pixel difference in one
 * row is not worth a look branch in the layout, and it is recorded as a bend rather than lost.
 */
function CardHead({
  className,
  row,
  children,
  ...props
}: React.ComponentProps<typeof View> &
  React.RefAttributes<View> & {
    /** Lay the band out as a row with its pieces pushed apart, rather than as a single title. */
    row?: boolean;
  }) {
  return (
    <TextClassContext.Provider value="text-band-ink">
      <View
        className={cn(
          'fit-band bg-band-foot -mx-4 -mt-4 mb-1 rounded-t-[9px] px-4 py-3',
          row && 'flex-row items-center justify-between gap-3',
          className,
        )}
        {...props}>
        {typeof children === 'string' ? (
          <Text variant="heading" className="text-band-ink">
            {children}
          </Text>
        ) : (
          children
        )}
      </View>
    </TextClassContext.Provider>
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<typeof Text>) {
  return <Text variant="heading" className={className} {...props} />;
}

/**
 * A card's own sub-line, and it stops at a readable measure rather than at the card's edge.
 *
 * The column cap keeps a card off the far side of a 1600px window; it does not stop the sentence
 * inside an 880px card from running the whole way across it, which is the same 120-character line
 * one level down. The 460px is `MEASURE_CLASS` in `panel.tsx` written out, because a primitive
 * importing from a layout component would be the dependency pointing the wrong way for one string.
 */
function CardDescription({ className, ...props }: React.ComponentProps<typeof Text>) {
  return <Text variant="small" className={cn('text-muted-foreground', 'max-w-[460px]', className)} {...props} />;
}

function CardContent({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  return <View className={cn('gap-3 p-4', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<typeof View> & React.RefAttributes<View>) {
  return <View className={cn('bg-background border-border flex-row items-center justify-end gap-2 border-t px-4 py-3', className)} {...props} />;
}

export { Card, CardContent, CardDescription, CardFooter, CardHead, CardHeader, CardTitle, Garland };
